import { useMemo } from 'react'
import { axiosBaseQuery, request } from 'react-common'
import { createApi } from '@reduxjs/toolkit/query/react'
import { GradingScale, LearnerObjectiveAccessFullModel, ObjectiveAccessesQueryModel } from '@/api/models/aggregate'
import { useAppSelector } from '../hooks'

export const aggregatesApi = createApi({
  reducerPath: 'aggregatesApi',
  baseQuery: axiosBaseQuery(request),
  tagTypes: ['Aggregates'],
  refetchOnFocus: true,
  endpoints: (builder) => ({
    queryObjectiveAccesses: builder.query<ObjectiveAccessesQueryModel, { query: Record<string, any> }>({
      query: ({ query }) => ({
        url: `users/me/objective-accesses/query`,
        method: 'get',
        params: {
          query: {
            sort: "[{ selector: 'objective.name', desc: false }]",
            requireTotalCount: true,
            ...query,
          },
        },
      }),
      providesTags: (result) =>
        result ? ['Aggregates', ...result.data.map(({ id }) => ({ type: 'Aggregates' as const, id }))] : ['Aggregates'],
    }),
    getObjectiveAccessById: builder.query<LearnerObjectiveAccessFullModel, string>({
      query: (id) => ({
        url: `users/me/objectives/${id}/objective-access`,
        method: 'get',
      }),
      providesTags: (result, error, id) => [{ type: 'Aggregates', id }],
    }),
    queryObjectiveAccessesForTerms: builder.query<ObjectiveAccessesQueryModel, { query: Record<string, any> }>({
      query: ({ query }) => ({
        url: `users/me/objective-accesses/query`,
        method: 'get',
        params: {
          query: {
            sort: "[{ selector: 'objective.name', desc: false }]",
            requireTotalCount: true,
            ...query,
          },
        },
      }),
    }),
    queryGradingScales: builder.query<{ data: GradingScale[] }, { query: Record<string, any> }>({
      query: ({ query }) => ({
        url: `grading-scales/query`,
        method: 'get',
        params: {
          query: {
            ...query,
          },
        },
      }),
    }),
  }),
})

export const useSelectObjectiveAccessById = (id: string) => {
  const selectObjectiveAccessById = useMemo(() => aggregatesApi.endpoints.getObjectiveAccessById.select(id), [id])

  return useAppSelector(selectObjectiveAccessById)
}

export const {
  useQueryObjectiveAccessesQuery,
  useQueryObjectiveAccessesForTermsQuery,
  useQueryGradingScalesQuery,
  useLazyQueryGradingScalesQuery,
  useGetObjectiveAccessByIdQuery,
  useLazyGetObjectiveAccessByIdQuery,
} = aggregatesApi
